import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { type ParticipantFocusApi } from "../../models/api";
import { getParticipantFocus } from "../../services";
import { setActiveLearningPath } from "../slices/activeLearningPath";

const ENDPOINT_URL = "/api/assessments/focus/participant-focus/$profileId";

type QueryParams = {
  profileId: string;
  focusId?: number;
};

type CacheParams = {
  profileId: string;
  focusId: number;
};

export const useApiParticipantFocus = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const useReactQuery = ({ profileId, focusId }: QueryParams) => {
    const queryKey = [ENDPOINT_URL, profileId, focusId];
    return useQuery({
      queryKey,
      queryFn: () => getParticipantFocus(profileId),
      enabled: !!profileId,
      onSuccess: (data) => {
        if (!focusId) {
          updateCache({ profileId, focusId: data.focusId }, data);
          dispatch(setActiveLearningPath(data.focusId));
        }
      },
    });
  };

  const updateCache = (
    { profileId, focusId }: CacheParams,
    updater:
      | ((oldData: ParticipantFocusApi) => ParticipantFocusApi)
      | ParticipantFocusApi
  ) => {
    const queryKey = [ENDPOINT_URL, profileId, focusId];
    const cache: ParticipantFocusApi = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(
      queryKey,
      typeof updater === "function" ? updater(cache) : updater
    );
  };

  return {
    useQuery: useReactQuery,
    updateCache,
  };
};
